/**
 * Chat Tab Component
 *
 * AI chat with conversation memory within the process/deadline context.
 * Messages are persisted to localStorage (will migrate to API later).
 * Has access to selected text from Word document.
 * Renders markdown in assistant responses.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Textarea,
  Spinner,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Delete24Regular,
  Dismiss12Regular,
  DocumentSearch24Regular,
  TextGrammarCheckmark24Regular,
  Checkmark16Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  Location16Regular,
  Warning16Regular,
  TextDescription24Regular,
  Edit24Regular,
  TextEditStyle24Regular,
  Clock16Regular,
  Copy16Regular,
  ArrowDownload16Regular,
  BrainCircuit24Regular,
} from "@fluentui/react-icons";
import { marked } from "marked";
import DOMPurify from "dompurify";

import { oliLlmClient, LLM_MODELS, getSelectedText, getParagraphsIndexed, getBodyParagraphsIndexed, searchAndSelectText, navigateToParagraphText, replaceInParagraph, replaceWithContext, createVisualDiff, findTextChanges, filterSignificantChanges, getWordContext, cleanupTrailingWhitespace, insertTextAtCursor, insertHtmlAtCursor, replaceSelection } from "../../../services";
import type { LlmModel, ChatMessage, TextChange } from "../../../services";
import { buildChatSystemPrompt, ANALYZE_TEXT_SYSTEM_PROMPT, REWRITE_SYSTEM_PROMPT, BASE_JURIDICAL_PROMPT, ACTION_PROMPTS } from "../../../config/prompts";
import { getFormattingPrompt } from "../../../services/formattingStyles";
import type { QuickActionType } from "../../../config/prompts";
import { Documento } from "../../../types";

// ============================================================
// Analysis Result Types (unified structure)
// ============================================================

interface AnalysisAjusteItem {
  paragraph: number; // Paragraph index [N] from AI (selection-relative)
  bodyParagraph: number; // Actual body paragraph index (for apply/locate)
  original: string;
  corrigido: string;
  occurrence: number; // Which occurrence in the paragraph (1-based)
  position: number; // Character position in original paragraph (for sorting)
  tipo: "erro" | "melhoria";
  motivo: string;
  applied?: boolean;
  rejected?: boolean;

  // Context-based application (new format)
  contexto?: string;     // Anchor text containing the target
  target?: string;       // Text to replace within context
  replacement?: string;  // Replacement text
  validationError?: string; // If set, blocks application with this error message
}

interface AnalysisJuridicoItem {
  observacao: string;
  sugestao?: string;
  referencia?: string;
}

/**
 * Group of corrections for a single paragraph
 */
interface ParagraphGroup {
  paragraph: number;        // Selection-relative index [N]
  bodyParagraph: number;    // Body index for operations
  originalFull: string;     // Full original paragraph text
  corrigidoFull: string;    // Full corrected paragraph text
  resumo: string;           // AI's summary for this paragraph
  ajustes: AnalysisAjusteItem[];  // Individual micro-changes
}

interface AnalysisResult {
  paragraphGroups: ParagraphGroup[];  // Grouped by paragraph
  ajustes: AnalysisAjusteItem[];      // Flat list (for backward compat)
  juridico: AnalysisJuridicoItem[];
  resumo: string;
  parseError?: string; // Track if there were parsing issues
  indexMap?: number[]; // Maps AI's selection indices to body indices
  // Metadata for display
  scope?: "selection" | "document";  // What was analyzed
  paragraphCount?: number;           // How many paragraphs
  timestamp?: string;                // When analysis was done
}

// ============================================================
// Validation Functions
// ============================================================

/**
 * AI's individual change with justification
 * Supports both new context-based format and legacy de/para format
 */
interface AIMudanca {
  // New context-based format (preferred)
  contexto?: string;    // Anchor text containing the target (must be unique in paragraph)
  target?: string;      // Exact text to replace within the context
  replacement?: string; // Corrected text to replace target with

  // Legacy format (fallback)
  de?: string;      // Original text
  para?: string;    // Corrected text

  // Common
  motivo: string;  // Justification for the change
}

/**
 * AI response format for paragraph-level corrections
 */
interface AIParagraphCorrection {
  paragraph: number;
  original: string;      // Full original paragraph
  corrigido: string;     // Full corrected paragraph
  mudancas?: AIMudanca[]; // Individual changes with justifications
  resumo?: string;       // AI's summary of changes (legacy fallback)
}

/**
 * Normalize degree/ordinal symbols to a single character.
 * ° (U+00B0 degree sign) and º (U+00BA ordinal) look identical but are different Unicode.
 * This causes "Target not found" errors when AI sends one and document has the other.
 */
function normalizeOrdinalSymbols(text: string): string {
  return text.replace(/°/g, "º"); // degree → ordinal
}

/**
 * Count occurrences of a substring in a string
 * For short targets (≤2 chars), uses word boundary matching to avoid
 * counting letters inside words (e.g., "a" in "visando" vs standalone "a")
 *
 * @param haystack - The string to search in
 * @param needle - The substring to find
 * @param useWordBoundary - If true and needle is short, use word boundary matching
 */
function countOccurrences(haystack: string, needle: string, useWordBoundary: boolean = false): number {
  if (!needle) return 0;

  // For short targets, use word boundary to match standalone words only
  if (useWordBoundary && needle.length <= 2) {
    // Escape special regex characters
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // \b = word boundary (matches between word and non-word characters)
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
    const matches = haystack.match(regex);
    return matches ? matches.length : 0;
  }

  // Standard substring counting for longer strings or when word boundary not needed
  let count = 0;
  let pos = 0;
  while ((pos = haystack.indexOf(needle, pos)) !== -1) {
    count++;
    pos += 1;
  }
  return count;
}

/**
 * Extract micro-ajustes from a paragraph pair
 * Supports two modes:
 * 1. NEW FORMAT: If mudancas have contexto/target/replacement, use directly (anchor-based)
 * 2. LEGACY FORMAT: If mudancas have de/para, use text diff extraction
 *
 * IMPORTANT: Uses ACTUAL document paragraph for validation to prevent AI hallucinations.
 */
function extractMicroAjustes(
  paragraphCorrection: AIParagraphCorrection,
  bodyParagraph: number,
  originalParagraphs: string[]
): AnalysisAjusteItem[] {
  const { paragraph, original: aiOriginal, corrigido, mudancas, resumo } = paragraphCorrection;

  // Get the ACTUAL document paragraph - this is the source of truth
  const actualParagraph = originalParagraphs[paragraph];

  if (!actualParagraph) {
    console.warn(`[Validation] Paragraph ${paragraph}: not found in document`);
    return [];
  }

  // Check if AI's original matches the document
  const aiMatchesDoc = actualParagraph.trim() === aiOriginal.trim();
  if (!aiMatchesDoc) {
    console.warn(`[Validation] Paragraph ${paragraph} mismatch - using document text for diff`);
    console.warn(`  Document: "${actualParagraph.substring(0, 50)}..."`);
    console.warn(`  AI sent:  "${aiOriginal.substring(0, 50)}..."`);
  }

  const sourceText = actualParagraph;
  const results: AnalysisAjusteItem[] = [];

  // Check if AI provided new context-based format
  const hasNewFormat = mudancas && mudancas.some(m => m.contexto && m.target && m.replacement);

  if (hasNewFormat && mudancas) {
    // NEW FORMAT: Process mudancas directly with contexto/target/replacement
    console.log(`[Validation] Paragraph ${paragraph}: using context-based format (${mudancas.length} mudancas)`);

    for (const m of mudancas) {
      if (!m.contexto || !m.target || !m.replacement) {
        // Skip mudancas without required fields
        console.warn(`[Validation] Skipping incomplete mudanca:`, m);
        continue;
      }

      // Normalize ordinal symbols (° vs º) for comparison
      // These are visually identical but different Unicode characters
      const normalizedContexto = normalizeOrdinalSymbols(m.contexto);
      const normalizedTarget = normalizeOrdinalSymbols(m.target);
      const normalizedSourceText = normalizeOrdinalSymbols(sourceText);

      const ajuste: AnalysisAjusteItem = {
        paragraph,
        bodyParagraph,
        original: m.contexto, // For display: show full context
        corrigido: m.contexto.replace(m.target, m.replacement),
        occurrence: 1, // Not used for context-based
        position: normalizedSourceText.indexOf(normalizedContexto), // For sorting (use normalized)
        tipo: m.target.length <= 20 ? "erro" : "melhoria",
        motivo: m.motivo,
        applied: false,
        // Context-based fields for application
        contexto: m.contexto,
        target: m.target,
        replacement: m.replacement,
      };

      // Validation 1: contexto contains target? (use normalized)
      if (!normalizedContexto.includes(normalizedTarget)) {
        console.warn(`[Validation] Contexto não contém target: "${m.contexto}" / "${m.target}"`);
        ajuste.validationError = `Contexto não contém target "${m.target}"`;
      }

      // Validation 2: target short without adequate context?
      if (m.target.length <= 2 && m.contexto === m.target) {
        console.warn(`[Validation] Target curto sem âncora: "${m.target}"`);
        ajuste.validationError = "Âncora insuficiente para mudança curta";
      }

      // Validation 3: contexto exists in paragraph? (use normalized)
      if (!normalizedSourceText.includes(normalizedContexto)) {
        console.warn(`[Validation] Contexto não encontrado no parágrafo: "${m.contexto.substring(0, 30)}..."`);
        ajuste.validationError = "Contexto não encontrado no parágrafo";
      }

      // Validation 4: contexto is unique in paragraph? (use normalized)
      const contextCount = countOccurrences(normalizedSourceText, normalizedContexto);
      if (contextCount > 1) {
        console.warn(`[Validation] Contexto aparece ${contextCount}x no parágrafo: "${m.contexto.substring(0, 30)}..."`);
        ajuste.validationError = `Contexto aparece ${contextCount}x (deve ser único)`;
      }

      // Validation 5: target is unique within contexto? (use normalized)
      // Use word boundary for short ALPHANUMERIC targets to avoid counting "a" inside "visando"
      // Don't use for punctuation/whitespace targets like " ," or ".." (word boundary doesn't apply)
      const isAlphanumericTarget = /^[\p{L}\p{N}]+$/u.test(m.target);
      const useWordBoundary = m.target.length <= 2 && isAlphanumericTarget;
      const targetCountInContext = countOccurrences(normalizedContexto, normalizedTarget, useWordBoundary);
      if (targetCountInContext === 0) {
        console.warn(`[Validation] Target não encontrado no contexto: "${m.target}"`);
        ajuste.validationError = `Target "${m.target}" não encontrado no contexto`;
      } else if (targetCountInContext > 1) {
        console.warn(`[Validation] Target aparece ${targetCountInContext}x no contexto`);
        ajuste.validationError = `Target aparece ${targetCountInContext}x no contexto (deve ser único)`;
      }

      results.push(ajuste);
    }

    return results;
  }

  // LEGACY FORMAT: Use diff-based extraction
  console.log(`[Validation] Paragraph ${paragraph}: using diff-based format`);

  // Compute diff between actual document and AI's corrected version
  const changes = findTextChanges(sourceText, corrigido);
  const significantChanges = filterSignificantChanges(changes, sourceText, corrigido);

  if (significantChanges.length === 0) {
    console.log(`[Validation] Paragraph ${paragraph}: no significant changes found`);
    return [];
  }

  console.log(`[Validation] Paragraph ${paragraph}: found ${significantChanges.length} micro-changes`);

  // Helper: find motivo from AI's mudancas array using POSITION-BASED matching
  const findMotivo = (change: TextChange, paragraphText: string): string => {
    if (mudancas && mudancas.length > 0) {
      for (const m of mudancas) {
        const searchText = m.contexto || m.de;
        if (!searchText) continue;

        let searchPos = 0;
        while (true) {
          const startPos = paragraphText.indexOf(searchText, searchPos);
          if (startPos === -1) break;
          const endPos = startPos + searchText.length;
          if (change.position >= startPos && change.position < endPos) {
            return m.motivo;
          }
          searchPos = startPos + 1;
        }
      }
    }
    if (resumo) return resumo;
    if (!change.improved) return `Remoção`;
    return `${change.original} → ${change.improved}`;
  };

  for (const change of significantChanges) {
    if (!change.original) {
      console.warn(`[Validation] Skipping insertion (no original text to search)`);
      continue;
    }

    if (!actualParagraph.includes(change.original)) {
      console.warn(`[Validation] Change "${change.original.substring(0, 30)}" not found in actual document - skipping`);
      continue;
    }

    const isSmallChange = change.original.length <= 20 && change.improved.length <= 20;
    const tipo: "erro" | "melhoria" = isSmallChange ? "erro" : "melhoria";
    const motivo = findMotivo(change, sourceText);

    // For short changes, expand context
    let searchOriginal = change.original;
    let searchCorrigido = change.improved;

    if (change.original.length <= 3) {
      const { wordOriginal, wordImproved } = getWordContext(change, sourceText);

      if (wordOriginal.length > change.original.length) {
        searchOriginal = wordOriginal;
        searchCorrigido = wordImproved;
      } else {
        const contextStart = Math.max(0, change.position - 1);
        const contextEnd = Math.min(sourceText.length, change.position + change.original.length + 1);
        const beforeChar = sourceText.substring(contextStart, change.position);
        const afterChar = sourceText.substring(change.position + change.original.length, contextEnd);

        if (/[\s.,;:!?]/.test(beforeChar) || /[\s.,;:!?]/.test(afterChar)) {
          searchOriginal = beforeChar + change.original + afterChar;
          searchCorrigido = beforeChar + change.improved + afterChar;
        }
      }

      if (searchOriginal !== change.original) {
        console.log(`[Validation] Expanded short change: "${change.original}" → "${searchOriginal}"`);
      }
    }

    let occurrence = 1;
    const beforeText = sourceText.substring(0, change.position);
    const matches = beforeText.split(searchOriginal).length - 1;
    occurrence = matches + 1;

    results.push({
      paragraph,
      bodyParagraph,
      original: searchOriginal,
      corrigido: searchCorrigido,
      occurrence,
      position: change.position,
      tipo,
      motivo,
      applied: false,
    });
  }

  return results;
}

/**
 * Validate a juridico item
 */
function validateJuridicoItem(item: unknown, index: number): AnalysisJuridicoItem | null {
  if (!item || typeof item !== "object") {
    console.warn(`[Validation] Juridico ${index}: not an object`);
    return null;
  }

  const obj = item as Record<string, unknown>;
  const observacao = typeof obj.observacao === "string" ? obj.observacao.trim() : "";

  if (!observacao) {
    console.warn(`[Validation] Juridico ${index}: empty observacao, skipping`);
    return null;
  }

  return {
    observacao,
    sugestao: typeof obj.sugestao === "string" ? obj.sugestao.trim() : undefined,
    referencia: typeof obj.referencia === "string" ? obj.referencia.trim() : undefined,
  };
}

/**
 * Parse and validate AI response into AnalysisResult
 * Uses new paragraph-based format with local diff extraction
 * @param content AI response content (JSON)
 * @param indexMap Maps AI's selection-relative indices to body indices
 * @param originalParagraphs Original paragraph texts for hash validation
 */
function parseAndValidateAnalysis(
  content: string,
  indexMap?: number[],
  originalParagraphs?: string[]
): AnalysisResult {
  const emptyResult: AnalysisResult = {
    paragraphGroups: [],
    ajustes: [],
    juridico: [],
    resumo: "Não foi possível processar a análise",
    parseError: undefined,
    indexMap,
  };

  if (!content || !content.trim()) {
    return { ...emptyResult, parseError: "Resposta vazia da IA" };
  }

  // Try to extract JSON from markdown code block
  let jsonStr = content;
  const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1];
  }

  // Try to find JSON object in the content
  const jsonObjectMatch = jsonStr.match(/\{[\s\S]*\}/);
  if (jsonObjectMatch) {
    jsonStr = jsonObjectMatch[0];
  }

  // Sanitize JSON: escape control characters inside string values
  // This handles cases where AI doesn't properly escape newlines/tabs
  const sanitizeJsonString = (str: string): string => {
    // Match strings in JSON and escape control characters within them
    return str.replace(/"([^"\\]|\\.)*"/g, (match) => {
      return match
        .replace(/[\x00-\x1f]/g, (char) => {
          const escapes: Record<string, string> = {
            '\n': '\\n', '\r': '\\r', '\t': '\\t',
            '\b': '\\b', '\f': '\\f'
          };
          return escapes[char] || `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`;
        });
    });
  };

  let parsed: Record<string, unknown>;
  try {
    const sanitized = sanitizeJsonString(jsonStr.trim());
    parsed = JSON.parse(sanitized);
  } catch (e) {
    console.error("[Validation] JSON parse error:", e);
    return { ...emptyResult, parseError: `JSON inválido: ${(e as Error).message}` };
  }

  if (!parsed || typeof parsed !== "object") {
    return { ...emptyResult, parseError: "Resposta não é um objeto JSON válido" };
  }

  // Handle paragraph-based format (paragrafos array)
  const rawParagrafos = Array.isArray(parsed.paragrafos) ? parsed.paragrafos : [];
  const validAjustes: AnalysisAjusteItem[] = [];
  const paragraphGroups: ParagraphGroup[] = [];

  for (const paraItem of rawParagrafos) {
    if (!paraItem || typeof paraItem !== "object") continue;

    const obj = paraItem as Record<string, unknown>;
    const paragraph = typeof obj.paragraph === "number" ? obj.paragraph : 0;
    const original = typeof obj.original === "string" ? obj.original : "";
    const corrigido = typeof obj.corrigido === "string" ? obj.corrigido : "";
    const resumo = typeof obj.resumo === "string" ? obj.resumo : "";
    // Extract mudancas array with motivos from AI response
    const mudancas = Array.isArray(obj.mudancas)
      ? (obj.mudancas as AIMudanca[])
      : undefined;

    // Skip if no actual change
    if (!original || !corrigido || original.trim() === corrigido.trim()) {
      console.log(`[Validation] Paragraph ${paragraph}: no changes, skipping`);
      continue;
    }

    // Compute body paragraph index
    let bodyParagraph = paragraph;
    if (indexMap && paragraph >= 0 && paragraph < indexMap.length) {
      const mapped = indexMap[paragraph];
      if (mapped < 0) {
        console.warn(`[Validation] Paragraph ${paragraph} not mapped to body index, skipping`);
        continue;
      }
      bodyParagraph = mapped;
    } else if (indexMap && paragraph >= indexMap.length) {
      console.warn(`[Validation] Paragraph ${paragraph} out of range (max ${indexMap.length - 1}), skipping`);
      continue;
    }

    // Get actual document paragraph for accurate diff and display
    const actualParagraph = originalParagraphs?.[paragraph] || original;

    // Extract micro-ajustes using diff (uses actual document text internally)
    const microAjustes = extractMicroAjustes(
      { paragraph, original, corrigido, mudancas, resumo },
      bodyParagraph,
      originalParagraphs || []
    );

    // Add to flat list
    validAjustes.push(...microAjustes);

    // Create paragraph group (even if no micro-ajustes extracted)
    // Use ACTUAL document paragraph for display, not AI's possibly-hallucinated version
    if (microAjustes.length > 0) {
      paragraphGroups.push({
        paragraph,
        bodyParagraph,
        originalFull: actualParagraph, // Use actual document text
        corrigidoFull: corrigido,
        resumo,
        ajustes: microAjustes,
      });
    }
  }

  // Validate juridico array (unchanged)
  const rawJuridico = Array.isArray(parsed.juridico) ? parsed.juridico : [];
  const validJuridico = rawJuridico
    .map((item, i) => validateJuridicoItem(item, i))
    .filter((item): item is AnalysisJuridicoItem => item !== null);

  // Log results
  console.log(`[Validation] Extracted ${validAjustes.length} micro-ajustes from ${paragraphGroups.length} paragraphs`);

  // Generate resumo from validated counts
  let finalResumo: string;
  if (validAjustes.length === 0 && validJuridico.length === 0) {
    finalResumo = "Sem ajustes necessários";
  } else {
    const erros = validAjustes.filter(a => a.tipo === "erro").length;
    const melhorias = validAjustes.filter(a => a.tipo === "melhoria").length;
    const parts: string[] = [];
    if (erros > 0) parts.push(`${erros} erro${erros > 1 ? "s" : ""}`);
    if (melhorias > 0) parts.push(`${melhorias} melhoria${melhorias > 1 ? "s" : ""}`);
    if (validJuridico.length > 0) parts.push(`${validJuridico.length} observação(ões) jurídica(s)`);
    finalResumo = parts.join(", ") || "Análise concluída";
  }

  return {
    paragraphGroups,
    ajustes: validAjustes,
    juridico: validJuridico,
    resumo: finalResumo,
    indexMap,
  };
}


// ============================================================
// Tool Response Types (for Redigir/Reescrever buttons)
// ============================================================

type ToolType = "redigir" | "reescrever";

interface ToolResponse {
  id: string;              // Unique ID for the response
  type: ToolType;          // Which tool was used
  title: string;           // Display title
  timestamp: string;       // ISO string
  inputPreview: string;    // Short preview of user input/selection
  result: string;          // The generated/rewritten text
  collapsed: boolean;      // Whether the block is collapsed
  applied?: boolean;       // Whether the result was applied to document
}

// Regex to match [[citation]] patterns
const CITATION_REGEX = /\[\[([^\]]+)\]\]/g;

// Configure marked for compact rendering
marked.setOptions({
  breaks: false,
  gfm: true,
});

interface Props {
  documento?: Documento | null;
}

interface ChatMessageUI {
  role: "user" | "assistant";
  content: string;
  timestamp: string; // ISO string for serialization
  analysisResult?: AnalysisResult; // Structured analysis for interactive rendering
}

// Storage key prefix
const STORAGE_PREFIX = "oli-word-chat:";

/**
 * Get storage key for a document's chat history
 * Uses prazo_sf_id if available, otherwise documento.id
 */
function getStorageKey(documento?: Documento | null): string {
  if (!documento) return `${STORAGE_PREFIX}external`;
  const contextId = documento.prazo_sf_id || documento.id;
  return `${STORAGE_PREFIX}${contextId}`;
}

/**
 * Load messages from localStorage
 */
function loadMessages(key: string): ChatMessageUI[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (e) {
    console.error("[ChatTab] Error loading messages:", e);
    return [];
  }
}

/**
 * Save messages to localStorage
 */
function saveMessages(key: string, messages: ChatMessageUI[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(messages));
  } catch (e) {
    console.error("[ChatTab] Error saving messages:", e);
  }
}

/**
 * Clear messages from localStorage
 */
function clearMessages(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("[ChatTab] Error clearing messages:", e);
  }
}

/**
 * Clear all old chat data (one-time migration to new format)
 */
function clearOldChatData(): void {
  const migrationKey = "oli-word-chat-v2-migrated";
  if (localStorage.getItem(migrationKey)) return; // Already migrated

  try {
    const keysToRemove = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
    keysToRemove.forEach(k => localStorage.removeItem(k));
    localStorage.setItem(migrationKey, "true");
    console.log("[ChatTab] Cleared old chat data for v2 migration");
  } catch (e) {
    console.error("[ChatTab] Error clearing old data:", e);
  }
}

// Run migration on load
clearOldChatData();

/**
 * Convert [[citation]] to clickable spans
 */
function processCitations(html: string): string {
  return html.replace(CITATION_REGEX, (_, citationText) => {
    // Escape HTML in citation text for safety
    const escaped = citationText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    return `<span class="chat-citation" data-citation="${escaped}" title="Clique para localizar no documento">${escaped}</span>`;
  });
}

/**
 * Render markdown to HTML with clickable citations
 */
function renderMarkdown(content: string): string {
  try {
    // First render markdown
    let html = marked.parse(content) as string;
    // Then process citations
    html = processCitations(html);
    // Sanitize to prevent XSS, but keep data-citation attribute
    html = DOMPurify.sanitize(html, { ADD_ATTR: ["data-citation"] });
    return html;
  } catch {
    return content;
  }
}

/**
 * Handle citation click - search and select in Word document
 */
async function handleCitationClick(citationText: string): Promise<void> {
  console.log("[ChatTab] Citation clicked:", citationText);
  const result = await searchAndSelectText(citationText);
  if (!result.success) {
    console.warn("[ChatTab] Citation not found:", result.error);
    // Could show a toast/notification here in the future
  }
}


// ============================================================
// Analysis Result Components
// ============================================================

/**
 * Compact row for individual correction within a paragraph group
 * Uses word context expansion for better readability of small changes
 */
function CompactAjusteRow({
  item,
  originalFull,
  index,
  onApply,
  onReject,
  onLocate,
  isApplying,
}: {
  item: AnalysisAjusteItem;
  originalFull: string; // Full paragraph text for word context expansion
  index: number; // 1-based index for display
  onApply: () => void;
  onReject: () => void;
  onLocate: () => void;
  isApplying?: boolean;
}) {
  // Create compact diff display with word context for small changes
  const diffHtml = useMemo(() => {
    // Build a TextChange-like object for getWordContext
    const change = {
      original: item.original,
      improved: item.corrigido,
      context: "",
      position: item.position,
    };

    // Expand to word boundaries for better display
    const { wordOriginal, wordImproved } = getWordContext(change, originalFull);

    return createVisualDiff(wordOriginal, wordImproved);
  }, [item.original, item.corrigido, item.position, originalFull]);

  // Determine state
  const isResolved = item.applied || item.rejected;
  const hasValidationError = !!item.validationError;

  return (
    <div className={`compact-ajuste-row ${item.applied ? "applied" : ""} ${item.rejected ? "rejected" : ""} ${hasValidationError ? "validation-error" : ""}`}>
      <span className="compact-ajuste-number">{index}.</span>
      <div className="compact-ajuste-content" onClick={onLocate} style={{ cursor: "pointer" }} title="Clique para localizar no documento">
        <div
          className="compact-ajuste-diff"
          dangerouslySetInnerHTML={{ __html: diffHtml }}
        />
        {item.motivo && (
          <div className="compact-ajuste-motivo">{item.motivo}</div>
        )}
        {hasValidationError && (
          <div className="compact-ajuste-validation-error">
            <Warning16Regular /> {item.validationError}
          </div>
        )}
      </div>
      <div className="compact-ajuste-action">
        {hasValidationError ? (
          <Tooltip content={item.validationError || "Erro de validação"} relationship="label">
            <span className="validation-error-badge">⚠️</span>
          </Tooltip>
        ) : !isResolved ? (
          <>
            <Button
              appearance="subtle"
              size="small"
              icon={<Checkmark16Regular />}
              onClick={onApply}
              disabled={isApplying}
              title="Aplicar"
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss12Regular />}
              onClick={onReject}
              disabled={isApplying}
              title="Recusar"
            />
          </>
        ) : item.applied ? (
          <span className="applied-badge-small">✓</span>
        ) : (
          <span className="rejected-badge-small">✗</span>
        )}
      </div>
    </div>
  );
}

/**
 * Paragraph group card - shows full paragraph diff with indented corrections
 */
function ParagraphGroupCard({
  group,
  onApplyAjuste,
  onRejectAjuste,
  onLocate,
  onLocateAjuste,
  isApplying,
}: {
  group: ParagraphGroup;
  onApplyAjuste: (ajusteIndex: number) => void;
  onRejectAjuste: (ajusteIndex: number) => void;
  onLocate: () => void;
  onLocateAjuste: (ajusteIndex: number) => void;
  isApplying?: boolean;
}) {
  // Create visual diff for the full paragraph
  const fullDiffHtml = useMemo(() => {
    return createVisualDiff(group.originalFull, group.corrigidoFull);
  }, [group.originalFull, group.corrigidoFull]);

  const appliedCount = group.ajustes.filter(a => a.applied).length;
  const rejectedCount = group.ajustes.filter(a => a.rejected).length;
  const pendingCount = group.ajustes.length - appliedCount - rejectedCount;
  const allResolved = pendingCount === 0;

  return (
    <div className={`paragraph-group ${allResolved ? "all-resolved" : ""}`}>
      {/* Header: Paragraph number + locate */}
      <div className="paragraph-group-header">
        <span className="paragraph-group-title">
          Parágrafo {group.paragraph + 1}
          <span className="paragraph-group-count">
            ({appliedCount}/{group.ajustes.length})
            {rejectedCount > 0 && <span className="rejected-count"> -{rejectedCount}</span>}
          </span>
        </span>
        <button
          className="analysis-locate-btn"
          onClick={onLocate}
          title="Localizar no documento"
        >
          <Location16Regular />
        </button>
      </div>

      {/* Explanation/Summary from AI */}
      {group.resumo && (
        <div className="paragraph-group-resumo">{group.resumo}</div>
      )}

      {/* Full paragraph diff */}
      <div
        className="paragraph-group-diff"
        dangerouslySetInnerHTML={{ __html: fullDiffHtml }}
      />

      {/* Individual corrections - compact rows */}
      <div className="paragraph-group-corrections">
        {group.ajustes.map((ajuste, i) => (
          <CompactAjusteRow
            key={i}
            item={ajuste}
            originalFull={group.originalFull}
            index={i + 1}
            onApply={() => onApplyAjuste(i)}
            onReject={() => onRejectAjuste(i)}
            onLocate={() => onLocateAjuste(i)}
            isApplying={isApplying}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Juridico observation item - simple list format
 */
function JuridicoItemCard({ item }: { item: AnalysisJuridicoItem }) {
  return (
    <li className="analysis-list-item">
      <span>{item.observacao}</span>
      {item.sugestao && (
        <>
          {" — "}
          <span className="analysis-suggestion">{item.sugestao}</span>
        </>
      )}
      {item.referencia && (
        <span className="analysis-reference"> [{item.referencia}]</span>
      )}
    </li>
  );
}

/**
 * Full analysis result card with unified structure
 */
function AnalysisResultCard({
  result,
  onUpdateResult,
  collapsed: externalCollapsed,
}: {
  result: AnalysisResult;
  onUpdateResult: (updated: AnalysisResult) => void;
  collapsed?: boolean;
}) {
  // Sections start collapsed
  const [expandedSections, setExpandedSections] = useState({
    ajustes: false,
    juridico: false,
  });
  const [isApplying, setIsApplying] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sync with external collapsed prop
  useEffect(() => {
    if (externalCollapsed !== undefined) {
      setIsCollapsed(externalCollapsed);
    }
  }, [externalCollapsed]);

  const toggleSection = (section: "ajustes" | "juridico") => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Format timestamp
  const formatTime = (isoString?: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleLocateText = async (bodyParagraph: number, _text: string, _occurrence: number) => {
    try {
      console.log(`[Analysis] Locating paragraph ${bodyParagraph}`);
      // Select the whole paragraph for better visualization
      const locateResult = await navigateToParagraphText(bodyParagraph, "", 1);
      if (locateResult.success) {
        console.log("[Analysis] Paragraph selected");
      } else {
        console.warn("[Analysis] Failed to locate paragraph:", locateResult.error);
        alert("Parágrafo não encontrado");
      }
    } catch (err) {
      console.error("[Analysis] Error locating paragraph:", err);
      alert("Erro ao buscar parágrafo no documento");
    }
  };

  /**
   * Locate and select a specific term (target) within the document
   * For context-based items: search for target within the paragraph
   * For legacy items: search for original text within the paragraph
   */
  const handleLocateAjuste = async (groupIndex: number, ajusteIndexInGroup: number) => {
    const group = paragraphGroups[groupIndex];
    const ajuste = group.ajustes[ajusteIndexInGroup];

    try {
      // Determine what text to search for
      // For short targets (≤2 chars like "a" for crase), search for the CONTEXT instead
      // This avoids finding "a" inside words like "para", "cada", etc.
      // For longer targets or legacy format, use target/original directly
      let searchText: string;
      if (ajuste.contexto && ajuste.target && ajuste.target.length <= 2) {
        // Short target: search for context to locate the exact position
        searchText = ajuste.contexto;
        console.log(`[Analysis] Using context for short target: "${searchText}"`);
      } else {
        searchText = ajuste.target || ajuste.original;
      }

      console.log(`[Analysis] Locating ajuste in paragraph ${ajuste.bodyParagraph}: "${searchText}"`);

      // Use navigateToParagraphText to find the specific text within the paragraph
      const locateResult = await navigateToParagraphText(
        ajuste.bodyParagraph,
        searchText,
        ajuste.occurrence
      );

      if (locateResult.success) {
        console.log("[Analysis] Term selected");
      } else {
        // Fallback: try selecting just the paragraph
        console.warn("[Analysis] Term not found, selecting paragraph:", locateResult.error);
        await navigateToParagraphText(ajuste.bodyParagraph, "", 1);
      }
    } catch (err) {
      console.error("[Analysis] Error locating term:", err);
    }
  };

  const handleApplyAjuste = async (index: number) => {
    if (isApplying) return;
    setIsApplying(true);

    try {
      const ajuste = result.ajustes[index];

      // Check for validation errors first
      if (ajuste.validationError) {
        alert(`Não é possível aplicar: ${ajuste.validationError}`);
        setIsApplying(false);
        return;
      }

      console.log(`[Analysis] Applying ajuste ${index} in body paragraph ${ajuste.bodyParagraph}: "${ajuste.original}" → "${ajuste.corrigido}"`);

      let replaceResult: { success: boolean; error?: string };

      // Use context-based replacement if available, otherwise legacy
      if (ajuste.contexto && ajuste.target && ajuste.replacement) {
        console.log(`[Analysis] Using context-based replacement: contexto="${ajuste.contexto}", target="${ajuste.target}" → "${ajuste.replacement}"`);
        replaceResult = await replaceWithContext(
          ajuste.bodyParagraph,
          ajuste.contexto,
          ajuste.target,
          ajuste.replacement
        );
      } else {
        console.log(`[Analysis] Using legacy replacement: "${ajuste.original}" → "${ajuste.corrigido}"`);
        replaceResult = await replaceInParagraph(
          ajuste.bodyParagraph,
          ajuste.original,
          ajuste.corrigido,
          ajuste.occurrence
        );
      }

      if (replaceResult.success) {
        const updated = { ...result };
        updated.ajustes = [...result.ajustes];
        updated.ajustes[index] = { ...ajuste, applied: true };

        // Also update paragraphGroups
        updated.paragraphGroups = result.paragraphGroups.map(group => {
          if (group.bodyParagraph === ajuste.bodyParagraph) {
            return {
              ...group,
              ajustes: group.ajustes.map(a =>
                a.original === ajuste.original && a.occurrence === ajuste.occurrence
                  ? { ...a, applied: true }
                  : a
              ),
            };
          }
          return group;
        });

        onUpdateResult(updated);

        // Navigate to next unapplied ajuste
        const nextUnapplied = updated.ajustes.findIndex(
          (a, i) => i > index && !a.applied
        );
        if (nextUnapplied !== -1) {
          const next = updated.ajustes[nextUnapplied];
          await navigateToParagraphText(next.bodyParagraph, "", 1);
        }
      } else {
        console.error("[Analysis] Failed to apply ajuste:", replaceResult.error);
        alert(`Falha ao aplicar correção:\n${replaceResult.error}\n\nTexto buscado: "${ajuste.original}"`);
      }
    } catch (err) {
      console.error("[Analysis] Exception applying ajuste:", err);
      alert(`Erro ao aplicar correção: ${err instanceof Error ? err.message : "Erro desconhecido"}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleApplyAll = async () => {
    if (isApplying) return;
    setIsApplying(true);

    const updated = { ...result };
    updated.ajustes = [...result.ajustes];
    updated.paragraphGroups = result.paragraphGroups.map(g => ({ ...g, ajustes: [...g.ajustes] }));
    let successCount = 0;
    let failCount = 0;
    const failedItems: string[] = [];

    try {
      // Group corrections by paragraph, then sort by position descending within each paragraph
      // This ensures we apply from END to START, preventing occurrence count shifts
      const byParagraph = new Map<number, number[]>();
      let skippedCount = 0;
      for (let i = 0; i < updated.ajustes.length; i++) {
        const ajuste = updated.ajustes[i];
        if (ajuste.applied) continue;
        // Skip items with validation errors
        if (ajuste.validationError) {
          skippedCount++;
          continue;
        }
        const indices = byParagraph.get(ajuste.bodyParagraph) || [];
        indices.push(i);
        byParagraph.set(ajuste.bodyParagraph, indices);
      }

      if (skippedCount > 0) {
        console.log(`[Analysis] Skipped ${skippedCount} items with validation errors`);
      }

      // Process each paragraph's corrections sorted by position descending
      for (const [, indices] of byParagraph) {
        // Sort by position descending (apply from end to start)
        indices.sort((a, b) => updated.ajustes[b].position - updated.ajustes[a].position);

        for (const i of indices) {
          const ajuste = updated.ajustes[i];

          let replaceResult: { success: boolean; error?: string };

          // Use context-based replacement if available, otherwise legacy
          if (ajuste.contexto && ajuste.target && ajuste.replacement) {
            replaceResult = await replaceWithContext(
              ajuste.bodyParagraph,
              ajuste.contexto,
              ajuste.target,
              ajuste.replacement
            );
          } else {
            replaceResult = await replaceInParagraph(
              ajuste.bodyParagraph,
              ajuste.original,
              ajuste.corrigido,
              ajuste.occurrence
            );
          }

          if (replaceResult.success) {
            updated.ajustes[i] = { ...ajuste, applied: true };
            // Also update in paragraphGroups
            for (const group of updated.paragraphGroups) {
              if (group.bodyParagraph === ajuste.bodyParagraph) {
                const idx = group.ajustes.findIndex(
                  a => a.original === ajuste.original && a.occurrence === ajuste.occurrence
                );
                if (idx !== -1) {
                  group.ajustes[idx] = { ...group.ajustes[idx], applied: true };
                }
              }
            }
            successCount++;
          } else {
            failCount++;
            failedItems.push(ajuste.original.substring(0, 30) + "...");
            console.warn(`[Analysis] Failed to apply ajuste ${i} in body paragraph ${ajuste.bodyParagraph}:`, replaceResult.error);
          }
        }
      }

      // Always update state with what succeeded
      onUpdateResult(updated);

      // Show feedback to user
      if (failCount > 0) {
        const failMsg = failedItems.slice(0, 3).join("\n• ");
        alert(`Aplicados: ${successCount}\nFalhas: ${failCount}\n\nNão encontrados:\n• ${failMsg}${failedItems.length > 3 ? "\n• ..." : ""}`);
      }
    } catch (e) {
      console.error("[Analysis] Error in Apply All:", e);
      // Still update state with partial results
      onUpdateResult(updated);
      alert(`Erro durante aplicação. ${successCount} aplicados com sucesso.`);
    } finally {
      setIsApplying(false);
    }
  };

  // Safe access with defaults for backward compatibility with old localStorage data
  const paragraphGroups = result.paragraphGroups || [];
  const ajustes = result.ajustes || [];
  const juridico = result.juridico || [];

  const ajustesCount = ajustes.length;
  const appliedCount = ajustes.filter((a) => a.applied).length;
  const rejectedCount = ajustes.filter((a) => a.rejected).length;
  const pendingAjustes = ajustes.filter((a) => !a.applied && !a.rejected);
  const paragraphCount = paragraphGroups.length;

  // Handler to reject an ajuste (mark as rejected without applying)
  const handleRejectAjuste = (index: number) => {
    const ajuste = result.ajustes[index];
    console.log(`[Analysis] Rejecting ajuste ${index}: "${ajuste.original}"`);

    const updated = { ...result };
    updated.ajustes = [...result.ajustes];
    updated.ajustes[index] = { ...ajuste, rejected: true };

    // Also update paragraphGroups
    updated.paragraphGroups = result.paragraphGroups.map(group => {
      if (group.bodyParagraph === ajuste.bodyParagraph) {
        return {
          ...group,
          ajustes: group.ajustes.map(a =>
            a.original === ajuste.original && a.occurrence === ajuste.occurrence
              ? { ...a, rejected: true }
              : a
          ),
        };
      }
      return group;
    });

    onUpdateResult(updated);
  };

  // Handler to apply an ajuste within a paragraph group
  const handleApplyGroupAjuste = async (groupIndex: number, ajusteIndexInGroup: number) => {
    const group = paragraphGroups[groupIndex];
    const ajuste = group.ajustes[ajusteIndexInGroup];

    // Find the index in the flat ajustes array
    const flatIndex = ajustes.findIndex(
      a => a.bodyParagraph === ajuste.bodyParagraph &&
           a.original === ajuste.original &&
           a.occurrence === ajuste.occurrence
    );

    if (flatIndex !== -1) {
      await handleApplyAjuste(flatIndex);
    }
  };

  // Handler to reject an ajuste within a paragraph group
  const handleRejectGroupAjuste = (groupIndex: number, ajusteIndexInGroup: number) => {
    const group = paragraphGroups[groupIndex];
    const ajuste = group.ajustes[ajusteIndexInGroup];

    // Find the index in the flat ajustes array
    const flatIndex = ajustes.findIndex(
      a => a.bodyParagraph === ajuste.bodyParagraph &&
           a.original === ajuste.original &&
           a.occurrence === ajuste.occurrence
    );

    if (flatIndex !== -1) {
      handleRejectAjuste(flatIndex);
    }
  };

  return (
    <div className={`analysis-result-card ${isCollapsed ? "collapsed" : ""}`}>
      {/* Header */}
      <div className="analysis-result-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="analysis-result-title">
          <TextGrammarCheckmark24Regular />
          <span>Correções</span>
          {result.timestamp && (
            <span className="analysis-result-time">
              <Clock16Regular />
              {formatTime(result.timestamp)}
            </span>
          )}
        </div>
        <span className="analysis-result-toggle">
          {isCollapsed ? <ChevronDown16Regular /> : <ChevronUp16Regular />}
        </span>
      </div>

      {/* Collapsed preview */}
      {isCollapsed && ajustesCount > 0 && (
        <div className="analysis-result-preview">
          {paragraphCount} parágrafo{paragraphCount > 1 ? "s" : ""} · {appliedCount}/{ajustesCount} aplicadas
        </div>
      )}

      {/* Content - visible when expanded */}
      {!isCollapsed && (
        <>
      {/* Ajustes Section - Grouped by paragraph */}
      {ajustesCount > 0 && (
        <div className="analysis-section">
          <div className="analysis-section-header">
            <span className="analysis-section-toggle" onClick={() => toggleSection("ajustes")}>
              {expandedSections.ajustes ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
            </span>
            <span className="analysis-section-title" onClick={() => toggleSection("ajustes")}>
              {paragraphCount} parágrafo{paragraphCount > 1 ? "s" : ""} ({appliedCount}/{ajustesCount} correções)
              {rejectedCount > 0 && <span className="rejected-count"> -{rejectedCount} recusadas</span>}
            </span>
            {pendingAjustes.length > 0 && (
              <Button
                appearance="primary"
                size="small"
                className="analysis-apply-all-btn"
                onClick={(e) => { e.stopPropagation(); handleApplyAll(); }}
                disabled={isApplying}
                style={{ marginLeft: "auto" }}
              >
                {isApplying ? "Aplicando..." : "Aplicar Todos"}
              </Button>
            )}
          </div>
          {expandedSections.ajustes && (
            <div className="analysis-section-content paragraph-groups">
              {paragraphGroups.map((group, gIdx) => (
                <ParagraphGroupCard
                  key={gIdx}
                  group={group}
                  onApplyAjuste={(ajusteIdx) => handleApplyGroupAjuste(gIdx, ajusteIdx)}
                  onRejectAjuste={(ajusteIdx) => handleRejectGroupAjuste(gIdx, ajusteIdx)}
                  onLocate={() => handleLocateText(group.bodyParagraph, "", 1)}
                  onLocateAjuste={(ajusteIdx) => handleLocateAjuste(gIdx, ajusteIdx)}
                  isApplying={isApplying}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Juridico Section */}
      {juridico.length > 0 && (
        <div className="analysis-section">
          <div className="analysis-section-header" onClick={() => toggleSection("juridico")}>
            <span className="analysis-section-toggle">
              {expandedSections.juridico ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
            </span>
            <span className="analysis-section-title">
              Observações Jurídicas ({juridico.length})
            </span>
          </div>
          {expandedSections.juridico && (
            <ul className="analysis-list">
              {juridico.map((item, i) => (
                <JuridicoItemCard key={i} item={item} />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Empty state */}
      {ajustesCount === 0 && juridico.length === 0 && (
        <div className="analysis-empty">Nenhum ajuste necessário.</div>
      )}
        </>
      )}
    </div>
  );
}

/**
 * Collapsible tool response block for Redigir/Reescrever results
 */
function ToolResponseBlock({
  response,
  onToggleCollapse,
  onApply,
  onCopy,
  isApplying,
}: {
  response: ToolResponse;
  onToggleCollapse: () => void;
  onApply: () => void;
  onCopy: () => void;
  isApplying?: boolean;
}) {
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toolLabels: Record<ToolType, { icon: React.ReactNode; label: string }> = {
    redigir: { icon: <Edit24Regular />, label: "Redigir texto" },
    reescrever: { icon: <TextEditStyle24Regular />, label: "Reescrever parágrafo" },
  };

  const { icon, label } = toolLabels[response.type];

  return (
    <div className={`tool-response-block ${response.collapsed ? "collapsed" : ""} ${response.applied ? "applied" : ""}`}>
      {/* Header - always visible */}
      <div className="tool-response-header" onClick={onToggleCollapse}>
        <div className="tool-response-title">
          {icon}
          <span>{label}</span>
          <span className="tool-response-time">
            <Clock16Regular />
            {formatTime(response.timestamp)}
          </span>
          {response.applied && <span className="applied-badge-small">✓ Aplicado</span>}
        </div>
        <span className="tool-response-toggle">
          {response.collapsed ? <ChevronDown16Regular /> : <ChevronUp16Regular />}
        </span>
      </div>

      {/* Input preview - always visible when collapsed */}
      {response.collapsed && (
        <div className="tool-response-preview">
          {response.inputPreview}
        </div>
      )}

      {/* Content - visible when expanded */}
      {!response.collapsed && (
        <div className="tool-response-content">
          {/* Input preview */}
          <div className="tool-response-input">
            <span className="tool-response-input-label">Entrada:</span>
            <span className="tool-response-input-text">{response.inputPreview}</span>
          </div>

          {/* Result text */}
          {response.type === "redigir" ? (
            <div
              className="tool-response-result tool-response-html"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(response.result) }}
            />
          ) : (
            <div className="tool-response-result">
              {response.result}
            </div>
          )}

          {/* Action buttons */}
          <div className="tool-response-actions">
            <Button
              appearance="primary"
              size="small"
              icon={<ArrowDownload16Regular />}
              onClick={(e) => { e.stopPropagation(); onApply(); }}
              disabled={isApplying || response.applied}
            >
              {response.type === "reescrever" ? "Substituir seleção" : "Inserir no documento"}
            </Button>
            <Button
              appearance="subtle"
              size="small"
              icon={<Copy16Regular />}
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
            >
              Copiar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Chat message component with markdown support and clickable citations
 */
function ChatMessageBubble({ msg, formatTime, onUpdateAnalysis, collapsed }: {
  msg: ChatMessageUI;
  formatTime: (ts: string) => string;
  onUpdateAnalysis?: (updated: AnalysisResult) => void;
  collapsed?: boolean;
}) {
  const htmlContent = useMemo(() => {
    if (msg.role === "assistant") {
      return renderMarkdown(msg.content);
    }
    return null;
  }, [msg.content, msg.role]);

  /**
   * Handle clicks on the markdown content - check if a citation was clicked
   */
  function handleContentClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    if (target.classList.contains("chat-citation")) {
      const citation = target.getAttribute("data-citation");
      if (citation) {
        e.preventDefault();
        handleCitationClick(citation);
      }
    }
  }

  // If this is an analysis result, render the card directly
  if (msg.role === "assistant" && msg.analysisResult && onUpdateAnalysis) {
    return (
      <AnalysisResultCard result={msg.analysisResult} onUpdateResult={onUpdateAnalysis} collapsed={collapsed} />
    );
  }

  return (
    <div className={`chat-message ${msg.role}`}>
      {msg.role === "assistant" && htmlContent ? (
        <div
          className="chat-message-content markdown-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          onClick={handleContentClick}
        />
      ) : (
        <div className="chat-message-content">{msg.content}</div>
      )}
      <div className="chat-message-time">{formatTime(msg.timestamp)}</div>
    </div>
  );
}

export function ChatTab({ documento }: Props) {
  const storageKey = getStorageKey(documento);

  // Initialize messages from localStorage
  const [messages, setMessages] = useState<ChatMessageUI[]>(() => loadMessages(storageKey));
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<LlmModel>("gemini-2.5-flash");
  const [toolResponses, setToolResponses] = useState<ToolResponse[]>([]);
  const [toolLoading, setToolLoading] = useState(false);
  const [activeToolMode, setActiveToolMode] = useState<ToolType | null>(null);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(60);
  const [collapseAllCards, setCollapseAllCards] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevMessageCountRef = useRef(messages.length);
  const prevToolResponseCountRef = useRef(toolResponses.length);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  // Handle resize drag for textarea
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = textareaHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const deltaY = startYRef.current - moveEvent.clientY; // Inverted for upward expansion
      const newHeight = Math.max(36, Math.min(300, startHeightRef.current + deltaY));
      setTextareaHeight(newHeight);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [textareaHeight]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(storageKey, messages);
    }
  }, [messages, storageKey]);

  // Scroll to bottom only when NEW messages are added (not on updates to existing messages)
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Scroll to bottom when new tool responses are added
  useEffect(() => {
    if (toolResponses.length > prevToolResponseCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevToolResponseCountRef.current = toolResponses.length;
  }, [toolResponses.length]);

  // Build system prompt with process context
  const systemPrompt = buildChatSystemPrompt({
    processNumber: documento?.processo_sf_id || undefined,
    deadline: documento?.prazo_sf_id || undefined,
    subject: documento?.doc_tipo || undefined,
  });

  /**
   * Clear the attached selection
   */
  function handleClearSelection() {
    setSelectedText(null);
  }

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;

    // Build message content with selection if present
    let messageContent = input.trim();
    if (selectedText) {
      messageContent = `[Texto selecionado do documento]\n---\n${selectedText}\n---\n\n${messageContent}`;
    }

    const userMessage: ChatMessageUI = {
      role: "user",
      content: messageContent,
      timestamp: new Date().toISOString(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setSelectedText(null); // Clear selection after sending
    setLoading(true);
    setError(null);

    try {
      // Build messages for API (including history)
      const apiMessages: ChatMessage[] = [
        { role: "system", content: systemPrompt },
        ...newMessages.map((m) => ({ role: m.role, content: m.content })),
      ];

      const response = await oliLlmClient.chat(apiMessages, {
        model: selectedModel,
        temperature: 0.7,
      });

      const assistantContent = oliLlmClient.getContent(response);

      const assistantMessage: ChatMessageUI = {
        role: "assistant",
        content: assistantContent,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao enviar mensagem");
      // Remove the user message on error so they can retry
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, selectedText, systemPrompt]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (activeToolMode === "redigir") {
        handleToolAction("redigir", input);
        setInput("");
        setActiveToolMode(null);
      } else {
        handleSend();
      }
    } else if (e.key === "Escape" && activeToolMode) {
      setActiveToolMode(null);
    }
  }

  function handleClearChat() {
    setMessages([]);
    clearMessages(storageKey);
    setError(null);
  }

  /**
   * Parse indexed text format "[0] text\n[1] text" into array of paragraph texts
   */
  function parseIndexedParagraphs(indexedText: string): string[] {
    const paragraphs: string[] = [];
    const lines = indexedText.split("\n");
    for (const line of lines) {
      // Match "[N] text" format and extract just the text
      const match = line.match(/^\[(\d+)\]\s*(.*)$/);
      if (match) {
        const index = parseInt(match[1], 10);
        const text = match[2];
        // Ensure array is large enough
        while (paragraphs.length <= index) {
          paragraphs.push("");
        }
        paragraphs[index] = text;
      }
    }
    return paragraphs;
  }

  /**
   * Handle quick action (Analyze Selection or Analyze All)
   * Uses paragraph-indexed text format for precise location
   */
  const handleQuickAction = useCallback(async (actionType: QuickActionType) => {
    if (loading) return;

    // Collapse all existing cards when starting new action
    setCollapseAllCards(true);
    // Also collapse tool responses
    setToolResponses((prev) => prev.map((r) => ({ ...r, collapsed: true })));

    // Set loading immediately so user sees feedback
    setLoading(true);
    setError(null);

    let paragraphData: { text: string; indexMap: number[] } | null = null;
    let autoCleanupCount = 0;

    // Auto-cleanup trailing whitespace BEFORE analysis to save tokens and ensure reliable fixes
    try {
      const scope = actionType === "analyze-selection" ? "selection" : "document";
      const cleanupResult = await cleanupTrailingWhitespace(scope);
      if (cleanupResult.fixed > 0) {
        autoCleanupCount = cleanupResult.fixed;
        console.log(`[Analysis] Auto-cleaned ${cleanupResult.fixed} trailing whitespace issues`);
      }
    } catch (e) {
      // Non-critical - continue even if cleanup fails
      console.warn("[Analysis] Whitespace cleanup failed:", e);
    }

    if (actionType === "analyze-selection") {
      // Get selected paragraphs with indices AND body index mapping
      try {
        const data = await getParagraphsIndexed();
        if (!data.text || !data.text.trim()) {
          setError("Selecione um texto no documento para analisar");
          setLoading(false);
          return;
        }
        paragraphData = data;
        console.log("[Analysis] Selection index map:", data.indexMap);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao obter seleção");
        setLoading(false);
        return;
      }
    } else {
      // Get entire document paragraphs with indices (identity mapping)
      try {
        const data = await getBodyParagraphsIndexed();
        if (!data.text || !data.text.trim()) {
          setError("Documento vazio");
          setLoading(false);
          return;
        }
        paragraphData = data;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao ler documento");
        setLoading(false);
        return;
      }
    }

    // Extract original paragraphs for hash validation
    const originalParagraphs = parseIndexedParagraphs(paragraphData.text);
    console.log("[Analysis] Parsed", originalParagraphs.length, "paragraphs for validation");

    // Count non-empty paragraphs for metadata
    const nonEmptyParagraphs = originalParagraphs.filter(p => p.trim().length > 0);
    const paragraphCount = nonEmptyParagraphs.length;
    const analysisScope = actionType === "analyze-selection" ? "selection" : "document";

    setSelectedText(null);

    try {
      // Use specific system prompt for analysis
      const apiMessages: ChatMessage[] = [
        { role: "system", content: ANALYZE_TEXT_SYSTEM_PROMPT },
        { role: "user", content: paragraphData.text },
      ];

      const response = await oliLlmClient.chat(apiMessages, {
        model: selectedModel,
        temperature: 0.3, // Lower temperature for structured output
        maxTokens: 50000, // Higher limit for long documents
      });

      const assistantContent = oliLlmClient.getContent(response);

      // Parse and validate analysis with index mapping AND original paragraphs for hash check
      const analysisResult = parseAndValidateAnalysis(
        assistantContent,
        paragraphData.indexMap,
        originalParagraphs
      );

      // Add metadata to the analysis result for display
      analysisResult.scope = analysisScope;
      analysisResult.paragraphCount = paragraphCount;
      analysisResult.timestamp = new Date().toISOString();

      // Determine display content
      let displayContent: string;
      if (analysisResult.parseError) {
        // Show error but also include raw response for debugging
        displayContent = `⚠️ ${analysisResult.parseError}\n\nResposta original:\n${assistantContent.substring(0, 500)}`;
        console.error("[ChatTab] Analysis parse error:", analysisResult.parseError);
      } else if (analysisResult.ajustes.length === 0 && analysisResult.juridico.length === 0) {
        displayContent = "✓ Texto analisado - nenhum ajuste necessário.";
      } else {
        displayContent = ""; // Card will show its own header
      }

      // Add auto-cleanup notification if fixes were made
      if (autoCleanupCount > 0) {
        const cleanupNote = `🧹 ${autoCleanupCount} correção(ões) automática(s) de espaçamento`;
        displayContent = displayContent ? `${cleanupNote}\n\n${displayContent}` : cleanupNote;
      }

      const assistantMessage: ChatMessageUI = {
        role: "assistant",
        content: displayContent,
        timestamp: new Date().toISOString(),
        analysisResult: analysisResult.parseError ? undefined : analysisResult,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      // Reset collapse state so the new card stays open
      setCollapseAllCards(false);
    } catch (e) {
      console.error("[ChatTab] Analysis error:", e);
      setError(e instanceof Error ? e.message : "Erro ao analisar texto");
      setCollapseAllCards(false);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, selectedText]);

  /**
   * Update analysis result in a message (for marking corrections as applied)
   */
  const handleUpdateAnalysis = useCallback((messageIndex: number, updatedResult: AnalysisResult) => {
    setMessages((prev) => {
      const updated = [...prev];
      updated[messageIndex] = { ...updated[messageIndex], analysisResult: updatedResult };
      return updated;
    });
  }, []);

  /**
   * Handle tool action (Redigir texto or Reescrever parágrafo)
   */
  const handleToolAction = useCallback(async (toolType: ToolType, userInput?: string) => {
    if (toolLoading) return;

    // Collapse all existing cards when starting new action
    setCollapseAllCards(true);

    setToolLoading(true);
    setError(null);

    try {
      let inputText: string;
      let inputPreview: string;

      if (toolType === "redigir") {
        // Redigir: requires user input
        if (!userInput?.trim()) {
          setError("Digite uma instrução para gerar o texto");
          setToolLoading(false);
          return;
        }
        inputText = userInput.trim();
        inputPreview = inputText.length > 80 ? inputText.substring(0, 80) + "..." : inputText;
      } else {
        // Reescrever: requires selected text
        const selected = await getSelectedText();
        if (!selected.trim()) {
          setError("Selecione um parágrafo no documento para reescrever");
          setToolLoading(false);
          return;
        }
        inputText = selected.trim();
        inputPreview = inputText.length > 80 ? inputText.substring(0, 80) + "..." : inputText;
      }

      // Build messages for API
      let messages: { role: "system" | "user"; content: string }[];

      if (toolType === "redigir") {
        const formattingInstructions = getFormattingPrompt("juridico");
        messages = [
          { role: "system", content: `${BASE_JURIDICAL_PROMPT}\n\n${formattingInstructions}` },
          { role: "user", content: `${ACTION_PROMPTS.gerar}\n\n${inputText}` },
        ];
      } else {
        messages = [
          { role: "system", content: REWRITE_SYSTEM_PROMPT },
          { role: "user", content: inputText },
        ];
      }

      const response = await oliLlmClient.chat(messages, {
        model: selectedModel,
        temperature: toolType === "redigir" ? 0.7 : 0.3,
      });

      const resultText = oliLlmClient.getContent(response);

      // Create new tool response
      const newResponse: ToolResponse = {
        id: `tool-${Date.now()}`,
        type: toolType,
        title: toolType === "redigir" ? "Redigir texto" : "Reescrever parágrafo",
        timestamp: new Date().toISOString(),
        inputPreview,
        result: resultText,
        collapsed: false,
      };

      // Auto-collapse previous responses and add new one
      setToolResponses((prev) => [
        ...prev.map((r) => ({ ...r, collapsed: true })),
        newResponse,
      ]);

      // Reset collapse state so existing analysis cards stay collapsed but new ones can open
      setCollapseAllCards(false);

      // Clear selected text state if it was used
      if (toolType === "reescrever") {
        setSelectedText(null);
      }
    } catch (e) {
      console.error("[ChatTab] Tool action error:", e);
      setError(e instanceof Error ? e.message : "Erro ao processar ferramenta");
      setCollapseAllCards(false);
    } finally {
      setToolLoading(false);
    }
  }, [toolLoading, selectedModel]);

  /**
   * Toggle collapse state of a tool response
   */
  const handleToggleToolCollapse = useCallback((responseId: string) => {
    setToolResponses((prev) =>
      prev.map((r) => (r.id === responseId ? { ...r, collapsed: !r.collapsed } : r))
    );
  }, []);

  /**
   * Apply tool result to document
   */
  const handleApplyToolResult = useCallback(async (responseId: string) => {
    const response = toolResponses.find((r) => r.id === responseId);
    if (!response || response.applied) return;

    try {
      if (response.type === "reescrever") {
        // Replace the current selection with the rewritten text
        await replaceSelection(response.result);
      } else {
        // Insert formatted HTML at cursor
        await insertHtmlAtCursor(response.result);
      }

      // Mark as applied
      setToolResponses((prev) =>
        prev.map((r) => (r.id === responseId ? { ...r, applied: true } : r))
      );
    } catch (e) {
      console.error("[ChatTab] Apply tool result error:", e);
      setError(e instanceof Error ? e.message : "Erro ao aplicar resultado");
    }
  }, [toolResponses]);

  /**
   * Copy tool result to clipboard
   */
  const handleCopyToolResult = useCallback(async (responseId: string) => {
    const response = toolResponses.find((r) => r.id === responseId);
    if (!response) return;

    try {
      await navigator.clipboard.writeText(response.result);
    } catch (e) {
      console.error("[ChatTab] Copy error:", e);
    }
  }, [toolResponses]);

  // Format timestamp for display
  function formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="chat-tab">
      {/* Chat Messages */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>Olá! Sou seu assistente jurídico.</p>
            <p style={{ fontSize: 12, color: "#605e5c", marginTop: 8 }}>
              Posso ajudar com dúvidas sobre o processo, redação de peças,
              análise de documentos e muito mais.
            </p>
            {documento?.processo_sf_id && (
              <p style={{ fontSize: 11, color: "#8a8886", marginTop: 12 }}>
                Processo: {documento.processo_sf_id}
              </p>
            )}
          </div>
        ) : (
          messages.map((msg, index) => (
            <ChatMessageBubble
              key={index}
              msg={msg}
              formatTime={formatTime}
              onUpdateAnalysis={msg.analysisResult ? (updated) => handleUpdateAnalysis(index, updated) : undefined}
              collapsed={collapseAllCards && index < messages.length - 1}
            />
          ))
        )}
        {/* Tool Responses */}
        {toolResponses.map((response) => (
          <ToolResponseBlock
            key={response.id}
            response={response}
            onToggleCollapse={() => handleToggleToolCollapse(response.id)}
            onApply={() => handleApplyToolResult(response.id)}
            onCopy={() => handleCopyToolResult(response.id)}
            isApplying={false}
          />
        ))}
        {loading && (
          <div className="chat-message assistant">
            <div className="chat-message-content">
              <Spinner size="tiny" /> Pensando...
            </div>
          </div>
        )}
        {toolLoading && (
          <div className="chat-message assistant">
            <div className="chat-message-content">
              <Spinner size="tiny" /> Processando...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="error-box" style={{ margin: "8px 0" }}>
          <p>{error}</p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="chat-quick-actions">
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content="Analisar texto e sugerir correções gramaticais" relationship="description">
              <Button
                className="quick-action-btn"
                appearance="outline"
                size="small"
                icon={<TextGrammarCheckmark24Regular />}
                disabled={loading || toolLoading}
              >
                Corrigir
              </Button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<TextDescription24Regular />}
                onClick={() => handleQuickAction("analyze-selection")}
              >
                Texto selecionado
              </MenuItem>
              <MenuItem
                icon={<DocumentSearch24Regular />}
                onClick={() => handleQuickAction("analyze-all")}
              >
                Documento inteiro
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <Tooltip content="Gerar novo texto a partir de instruções" relationship="description">
          <Button
            className={`quick-action-btn ${activeToolMode === "redigir" ? "active" : ""}`}
            appearance={activeToolMode === "redigir" ? "primary" : "outline"}
            size="small"
            icon={<Edit24Regular />}
            disabled={loading || toolLoading}
            onClick={() => setActiveToolMode(activeToolMode === "redigir" ? null : "redigir")}
          >
            Redigir
          </Button>
        </Tooltip>

        <Tooltip content="Reescrever o parágrafo selecionado de forma mais clara" relationship="description">
          <Button
            className="quick-action-btn"
            appearance="outline"
            size="small"
            icon={<TextEditStyle24Regular />}
            disabled={loading || toolLoading}
            onClick={() => handleToolAction("reescrever")}
          >
            Reescrever
          </Button>
        </Tooltip>
      </div>


      {/* Selected Text Preview */}
      {selectedText && (
        <div className="chat-selection-preview">
          <div className="chat-selection-header">
            <span>Texto selecionado</span>
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss12Regular />}
              onClick={handleClearSelection}
              title="Remover seleção"
            />
          </div>
          <div className="chat-selection-text">
            {selectedText.length > 200 ? selectedText.substring(0, 200) + "..." : selectedText}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="chat-input-area">
        {/* Resize handle at top */}
        <div
          className="chat-input-resize-handle"
          onMouseDown={handleResizeMouseDown}
          title="Arraste para redimensionar"
        />
        <div className="chat-input-row">
          <Textarea
            ref={textareaRef}
            placeholder={activeToolMode === "redigir"
              ? "Descreva o que deseja gerar... Ex: 'Crie uma petição inicial de cobrança'"
              : "Digite sua mensagem..."}
            value={input}
            onChange={(_, data) => setInput(data.value)}
            onKeyDown={handleKeyDown}
            disabled={loading || toolLoading}
            style={{ flex: 1, height: textareaHeight }}
          />
          <div className="chat-buttons">
            <Tooltip content={activeToolMode === "redigir" ? "Gerar texto (Enter)" : "Enviar (Enter)"} relationship="label">
              <Button
                appearance="primary"
                size="small"
                icon={loading || toolLoading ? <Spinner size="tiny" /> : <Send24Regular />}
                onClick={() => {
                  if (activeToolMode === "redigir") {
                    handleToolAction("redigir", input);
                    setInput("");
                    setActiveToolMode(null);
                  } else {
                    handleSend();
                  }
                }}
                disabled={loading || toolLoading || !input.trim()}
              />
            </Tooltip>
            {/* Model Selector */}
            <Dialog open={modelDialogOpen} onOpenChange={(_, data) => setModelDialogOpen(data.open)}>
              <DialogTrigger disableButtonEnhancement>
                <Tooltip content={`Modelo: ${LLM_MODELS.find(m => m.id === selectedModel)?.label || selectedModel}`} relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<BrainCircuit24Regular />}
                    disabled={loading}
                  />
                </Tooltip>
              </DialogTrigger>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>Selecionar Modelo</DialogTitle>
                  <DialogContent>
                    <RadioGroup
                      value={selectedModel}
                      onChange={(_, data) => {
                        setSelectedModel(data.value as LlmModel);
                        setModelDialogOpen(false);
                      }}
                    >
                      {LLM_MODELS.map((m) => (
                        <Radio key={m.id} value={m.id} label={m.label} />
                      ))}
                    </RadioGroup>
                  </DialogContent>
                </DialogBody>
              </DialogSurface>
            </Dialog>

            <Tooltip content="Limpar conversa" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                onClick={handleClearChat}
                disabled={loading || messages.length === 0}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
